import {
  AggregationGroupType,
  DEBUG_OVERRIDE,
  DataLifeCyclePeriod,
  Group,
  HistoryStore,
  IDailyStateHistory,
  Milliseconds,
  ModelTypes,
  NOT_LAMBDA,
  UserAttributeDefs,
  ValidModelType,
} from '@iotv/datamodel-core'
import {
  CommonBusinessObject,
  HistoryObjectType,
  HistoryStoreSystemAtts,
  MapLike,
  PossibleBusinessObject,
  StateSnapshot,
  ValidBusinessObjectList,
} from '@iotv/iotv-v3-types'
import { aggregateMean } from './AggregationFunctions/defaultAggregationFns'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

type AggregationGroupProps = PossibleBusinessObject & {
  dailyStateHistory?: HistoryStore
  supportedType?: ValidModelType | undefined
}

export interface IAggregationGroup {
  dailyStateHistory: HistoryStore
  supportedType: ValidModelType | undefined
  getSupportedTypeClass: (...args: any) => ModelTypes
  getStateKeys: () => MapLike<any>
  getSupportedTypeStateKeys: () => string[]
  getAggregationFns: () => MapLike<Function>
  aggregateMetrics: (groupedItems: ValidBusinessObjectList, group?: AggregationGroupType) => void
  getStateSnapshot: (aggregationKeys: string[]) => StateSnapshot<any>
  updateHistory: (aggregationKeys: string[]) => void
  getUserAttributes: () => UserAttributeDefs
}

const AggregationGroupFactory = (ModelClasses: any) =>
  class AggregationGroup extends Group implements IDailyStateHistory {
    static typeName = 'AggregationGroup' as ValidModelType

    static defaultDailyHistoryObjectSystemProperties: HistoryStoreSystemAtts = {
      autoTrim: true,
      maxAgeMS: Milliseconds.DAY * 50,
      maxLength: 50,
    }

    static getDefaultHistoryObjectParams = (period: DataLifeCyclePeriod): Partial<HistoryObjectType> => {
      let systemProperties: HistoryStoreSystemAtts
      switch (period) {
        case DataLifeCyclePeriod.DAILY:
          systemProperties = AggregationGroup.defaultDailyHistoryObjectSystemProperties
          break

        default:
          systemProperties = AggregationGroup.defaultDailyHistoryObjectSystemProperties
      }

      return {
        index: [],
        timestamp: [],
        systemProperties,
      }
    }

    constructor({ dailyStateHistory, ...ob }: AggregationGroupProps) {
      super({ ...ob, type: ob.type ?? AggregationGroup.typeName })
      const stateAttKeys = this.getSupportedTypeStateKeys()
      stateAttKeys?.forEach((k) => !this[k] && (this[k] = undefined))
      const params = { ...AggregationGroup.getDefaultHistoryObjectParams(DataLifeCyclePeriod.DAILY), ...dailyStateHistory }
      this.dailyStateHistory = new HistoryStore(params)
    }

    dailyStateHistory: HistoryStore

    // 2024 09 using `declare` as this will be initialised and exist on the underlying Group object
    declare supportedType: ValidModelType | undefined

    getSupportedTypeClass = () => this.supportedType && ModelClasses[this.supportedType]

    getStateKeys = () => {
      const stateKeys = [...this.getSupportedTypeStateKeys()]
      debug && console.log('Aggregation Group State keys', stateKeys)
      return stateKeys
    }

    getSupportedTypeStateKeys = () => {
      let stateAttKeys: string[] = []
      if (this.supportedType) {
        let instance: CommonBusinessObject | undefined = undefined
        if (ModelClasses[this.supportedType]) {
          instance = new ModelClasses[this.supportedType]({})
        }
        if (instance && typeof instance.getStateKeys === 'function') {
          stateAttKeys = instance.getStateKeys()
        }
      }
      debug && console.log({ supType: this.supportedType, stateAttKeys })
      return stateAttKeys
    }

    getAggregationFns = () => {
      let aggregationFns = undefined
      const supportedTypeClass = this.getSupportedTypeClass()

      if (supportedTypeClass && (supportedTypeClass as MapLike<any | Function>).aggregationFns) {
        aggregationFns = (supportedTypeClass as MapLike<any | Function>).aggregationFns
      }
      return aggregationFns
    }

    aggregateMetrics = (groupedItems: ValidBusinessObjectList, group?: AggregationGroupType) => {
      const stateAttKeys = this.getSupportedTypeStateKeys()
      const supportedTypeClass = this.getSupportedTypeClass()
      const aggregationFns = this.getAggregationFns()
      if (stateAttKeys && supportedTypeClass) {
        const supportedTypeInstances = groupedItems.map((item) => new supportedTypeClass(item)).filter((instance) => instance)

        stateAttKeys.forEach((k) => {
          if (aggregationFns && aggregationFns[k]) {
            this[k] = aggregationFns[k].call(this, supportedTypeInstances, group)
          } else {
            this[k] = aggregateMean(supportedTypeInstances, k)
            // on the basis that mean is more often appropriate than total
          }
        })
        this.updateHistory(stateAttKeys)
      }
    }

    getStateSnapshot = (aggregationKeys: string[]): StateSnapshot<any> => {
      const timestamp: number = Date.now()

      const state = aggregationKeys.reduce((acc, k) => Object.assign(acc, { [k]: this[k] }), {})
      return { timestamp, state }
    }

    updateHistory = (aggregationKeys: string[]) => {
      const stateSnapshot: StateSnapshot<any> = this.getStateSnapshot(aggregationKeys)
      this.dailyStateHistory.push(stateSnapshot)
    }

    getUserAttributes(): UserAttributeDefs {
      const { Group, Ruleset, Horse, Silo, Tank, ...retained } = super.getUserAttributes()

      return {
        ...retained,
      }
    }
  }

export { AggregationGroupFactory }
